import { useAction, useApi } from '../../api/hooks.js';
import {
  disableProvider,
  enableProvider,
  getProviderHealth,
  getReviewQueue,
  resetBreaker,
  type ProviderHealth,
} from '../api.js';
import { Bars, Dumbbell, Meter } from '../charts.js';
import { toNumber } from '../charts-math.js';
import { fmtPct } from '../forms.js';
import { alarmLabel, breakerLabel, fmtMoney, fmtWhen, hoursText, windowLabel } from '../labels.js';
import { adminHash } from '../routes.js';
import { Card, Empty, Head, Lanes, Loading, ReasonStrip, show, Tag, Trouble } from '../ui.js';

/**
 * The overview: every search provider's switch, health and spend, the alarms
 * firing across them, and how much review work is waiting. Reads refresh
 * every 30 seconds while the tab is visible (useApi's rule).
 */

const OVERVIEW_POLL_MS = 30_000;

type ProviderVerb = 'disable' | 'enable' | 'reset';

/**
 * A provider that made calls but recorded no cost has no price per call on the
 * services side (Hive is contract-priced and its price is deliberately unset —
 * services CLAUDE.md §7.6), so its spend is UNKNOWN, not zero. The health
 * payload carries no price field, so this is read off the figures it does
 * carry: successful calls, and a cost that is exactly nothing.
 */
function unpriced(provider: ProviderHealth): boolean {
  const calls = toNumber(provider.successful_calls_24h) ?? 0;
  const today = toNumber(provider.cost_usd);
  const month = toNumber(provider.month_to_date_cost_usd);
  return calls > 0 && today === 0 && month === 0;
}

/** Money as text for a chart label; anything that is not a decimal string reads as a dash. */
function moneyText(value: unknown): string {
  return typeof value === 'string' ? fmtMoney(value) : '—';
}

function breakerTone(state: unknown): 'ok' | 'warn' | 'bad' | undefined {
  if (state === 'closed') return 'ok';
  if (state === 'half_open') return 'warn';
  if (state === 'open') return 'bad';
  return undefined;
}

export function OverviewScreen() {
  const health = useApi(getProviderHealth, [], { pollMs: OVERVIEW_POLL_MS });
  const queue = useApi(getReviewQueue, [], { pollMs: OVERVIEW_POLL_MS });

  const act = useAction(
    (verb: ProviderVerb, providerId: string, reason: string) =>
      verb === 'disable'
        ? disableProvider(providerId, reason)
        : verb === 'enable'
          ? enableProvider(providerId, reason)
          : resetBreaker(providerId, reason),
    () => health.reload(),
  );

  const providers = health.data?.providers ?? [];
  // The services' rolling window for calls, success rate and latency (1 hour on
  // dev); OK (24h) is always a day. Both are named wherever they appear.
  const win = windowLabel(health.data?.window_hours);
  const winText = hoursText(health.data?.window_hours);
  const alarms = providers.flatMap((provider) =>
    (provider.alarms ?? []).map((alarm) => ({ ...alarm, provider_id: alarm.provider_id ?? provider.provider_id })),
  );

  return (
    <>
      <Head
        title="Overview"
        lede="The search providers ImageShield relies on, any alarms firing on them, and how much review work is waiting. Costs are in US dollars."
        meta={
          health.data !== null && (
            <span>
              Updated {fmtWhen(health.data.as_of)} · calls, success and response time cover {winText}; OK is
              the last 24 hours
            </span>
          )
        }
      />

      <Card title="Alarms">
        {health.error !== null ? (
          <Trouble error={health.error} />
        ) : health.data === null ? (
          <Loading />
        ) : alarms.length === 0 ? (
          <Empty>No alarms. Every provider is behaving.</Empty>
        ) : (
          <div className="cr-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Provider</th>
                  <th>Alarm</th>
                  <th>Detail</th>
                </tr>
              </thead>
              <tbody>
                {alarms.map((alarm, index) => (
                  <tr key={`${alarm.provider_id}-${alarm.kind}-${String(index)}`} className="flag">
                    <td>{alarm.provider_id}</td>
                    <td>
                      <Tag tone="bad">{alarmLabel(alarm.kind)}</Tag>
                    </td>
                    <td>{alarm.detail}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {health.data !== null && providers.length > 0 && (
        <div className="cr-grid-2">
          <Card title="Spend against budget">
            <p className="cr-card-note">
              Today's spend against each provider's daily budget, and this month's against its monthly one. Amber
              from 80% of a budget; red at or over it. A provider with no price on file records every call at
              $0.00, so its spend is unknown rather than zero.
            </p>
            {providers.map((provider) => (
              <div key={provider.provider_id} className="viz-group">
                <div className="viz-group-title">
                  {provider.provider_id}
                  {unpriced(provider) && (
                    <>
                      {' '}
                      <Tag tone="warn">No price on file — spend not tracked</Tag>
                    </>
                  )}
                </div>
                <Meter
                  label="Today"
                  spend={provider.cost_usd}
                  budget={provider.daily_budget_usd}
                  spendText={moneyText(provider.cost_usd)}
                  budgetText={moneyText(provider.daily_budget_usd)}
                />
                <Meter
                  label="This month"
                  spend={provider.month_to_date_cost_usd}
                  budget={provider.monthly_budget_usd}
                  spendText={moneyText(provider.month_to_date_cost_usd)}
                  budgetText={moneyText(provider.monthly_budget_usd)}
                />
              </div>
            ))}
          </Card>
          <Card title="Activity, last 24 hours">
            <p className="cr-card-note">
              Successful calls per provider in the last 24 hours; the success rate beside each is over {winText}.
            </p>
            <Bars
              ariaLabel="Successful calls in the last 24 hours, by provider"
              format={(value) => String(value)}
              rows={providers.map((provider) => ({
                key: provider.provider_id,
                label: provider.provider_id,
                value: toNumber(provider.successful_calls_24h),
                hint:
                  typeof provider.success_rate === 'number'
                    ? `· ${fmtPct(provider.success_rate)} success${win === '' ? '' : ` (${win})`}`
                    : undefined,
              }))}
            />
            <h3>Response time{win === '' ? '' : ` (${win})`}</h3>
            <Dumbbell
              ariaLabel="Response time per provider, typical to slowest one percent"
              format={(value) => `${String(value)} ms`}
              loLabel="Typical (median)"
              hiLabel="Slowest 1%"
              rows={providers.map((provider) => ({
                key: provider.provider_id,
                label: provider.provider_id,
                lo: toNumber(provider.latency_p50_ms),
                hi: toNumber(provider.latency_p99_ms),
              }))}
            />
          </Card>
        </div>
      )}

      <Card
        title="Search providers"
        aside={
          act.result !== null && (
            <Tag tone="ok">
              {act.result.provider_id}:{' '}
              {act.result.enabled === null ? breakerLabel(act.result.breaker_state) : act.result.enabled ? 'switched on' : 'switched off'}
            </Tag>
          )
        }
      >
        <p className="cr-card-note">
          Switching a provider off stops sending it searches; it is skipped, not failed. Health shows whether
          its automatic protection has tripped after repeated errors — a tripped provider is paused until
          its cooldown ends, or until you restore it.
        </p>
        {health.data !== null &&
          (providers.length === 0 ? (
            <Empty>No providers are configured.</Empty>
          ) : (
            <div className="cr-table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Provider</th>
                    <th>Status</th>
                    <th>Health</th>
                    <th className="num">Calls{win === '' ? '' : ` (${win})`}</th>
                    <th className="num">Success{win === '' ? '' : ` (${win})`}</th>
                    <th className="num">OK (24h)</th>
                    <th className="num">Latency p50 / p99{win === '' ? '' : ` (${win})`}</th>
                    <th className="num">Cost today</th>
                    <th className="num">Daily budget</th>
                    <th className="num">Headroom</th>
                    <th className="num">Month to date</th>
                    <th className="num">Monthly budget</th>
                  </tr>
                </thead>
                <tbody>
                  {providers.map((provider) => (
                    <ProviderRows key={provider.provider_id} provider={provider} act={act} />
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        {act.error !== null && <Trouble error={act.error} />}
      </Card>

      <Card
        title="Review queue"
        aside={
          <a href={adminHash('review')} className="cr-btn ghost">
            Go to review
          </a>
        }
      >
        {queue.error !== null ? (
          <Trouble error={queue.error} />
        ) : queue.data === null ? (
          <Loading />
        ) : (
          <Lanes queue={queue.data} />
        )}
      </Card>
    </>
  );
}

function ms(value: unknown) {
  return typeof value === 'number' ? `${String(value)} ms` : show(value);
}

function ProviderRows({
  provider,
  act,
}: {
  provider: ProviderHealth;
  act: { run: (verb: ProviderVerb, providerId: string, reason: string) => void; pending: boolean };
}) {
  const tripped = provider.breaker_state !== undefined && provider.breaker_state !== 'closed';
  const flagged = provider.enabled === false || tripped;
  const money = (value: unknown) => (typeof value === 'string' ? fmtMoney(value) : show(value));
  return (
    <>
      <tr className={flagged ? 'flag' : undefined}>
        <td>
          <strong>{provider.provider_id}</strong>
        </td>
        <td>
          <Tag tone={provider.enabled ? 'ok' : 'bad'}>{provider.enabled ? 'On' : 'Off'}</Tag>
        </td>
        <td>
          <Tag tone={breakerTone(provider.breaker_state)}>{breakerLabel(provider.breaker_state)}</Tag>
          {typeof provider.breaker_reason === 'string' && provider.breaker_reason !== '' && (
            <div className="cr-fine">{provider.breaker_reason}</div>
          )}
        </td>
        <td className="num">{show(provider.window_call_count)}</td>
        <td className="num">
          {typeof provider.success_rate === 'number' || provider.success_rate === null
            ? fmtPct(provider.success_rate)
            : show(provider.success_rate)}
        </td>
        <td className="num">{show(provider.successful_calls_24h)}</td>
        <td className="num">
          {ms(provider.latency_p50_ms)} / {ms(provider.latency_p99_ms)}
        </td>
        <td className="num" title={unpriced(provider) ? 'No price per call is set for this provider, so its spend is not tracked' : typeof provider.cost_usd === 'string' ? provider.cost_usd : undefined}>
          {unpriced(provider) ? <span className="cr-muted">not tracked</span> : money(provider.cost_usd)}
        </td>
        <td className="num">{money(provider.daily_budget_usd)}</td>
        <td className="num">{money(provider.budget_headroom_usd)}</td>
        <td className="num" title={unpriced(provider) ? 'No price per call is set for this provider, so its spend is not tracked' : typeof provider.month_to_date_cost_usd === 'string' ? provider.month_to_date_cost_usd : undefined}>
          {unpriced(provider) ? <span className="cr-muted">not tracked</span> : money(provider.month_to_date_cost_usd)}
        </td>
        <td className="num">{money(provider.monthly_budget_usd)}</td>
      </tr>
      <tr>
        <td colSpan={12} style={{ paddingTop: 0 }}>
          {provider.enabled ? (
            <ReasonStrip
              verb="Switch off"
              tone="danger"
              pending={act.pending}
              placeholder="Why searches should stop going to this provider"
              onRun={(reason) => act.run('disable', provider.provider_id, reason)}
            />
          ) : (
            <ReasonStrip
              verb="Switch on"
              tone="primary"
              pending={act.pending}
              placeholder="Why this provider can receive searches again"
              onRun={(reason) => act.run('enable', provider.provider_id, reason)}
            />
          )}
          {tripped && (
            <ReasonStrip
              verb="Restore now"
              tone="primary"
              pending={act.pending}
              placeholder="Why the cooldown can be skipped — e.g. the vendor confirmed the incident is over"
              onRun={(reason) => act.run('reset', provider.provider_id, reason)}
            />
          )}
        </td>
      </tr>
    </>
  );
}
